import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { LOGO_DARK_BG, BG_IMAGE } from "../lib/branding";
import { ArrowRight, Clock, BarChart3, Users, Shield } from "lucide-react";

const LOGO_URL = LOGO_DARK_BG;
const LOGO_DISPLAY = "https://customer-assets.emergentagent.com/job_bc07a5b0-4401-454c-9821-20d81dadd6da/artifacts/21c8lw1c_fa793a21-0229-4214-a16b-e1c0df8a86d3.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Inloggningen misslyckades");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Clock, label: "Tidrapportering", desc: "Snabb & enkel" },
    { icon: BarChart3, label: "Projektuppföljning", desc: "Realtidsdata" },
    { icon: Users, label: "Teamplanering", desc: "Smart schema" },
    { icon: Shield, label: "Dokument", desc: "Säker lagring" },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#060a10' }}>
      {/* Left - Brand */}
      <div className="hidden lg:flex lg:w-[55%] relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={BG_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(8,12,18,0.9) 0%, rgba(109,191,74,0.05) 50%, rgba(8,12,18,0.95) 100%)' }} />
        </div>

        {/* Ambient glow */}
        <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full blur-[120px] opacity-20" style={{ background: 'radial-gradient(circle, #6dbf4a 0%, transparent 70%)' }} />

        <div className="relative z-10 flex flex-col items-center px-12 max-w-lg">
          <div className="mb-14">
            <img src={LOGO_DISPLAY} alt="Mälair Installation AB" className="w-64 h-auto drop-shadow-2xl" style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }} />
          </div>

          <div className="grid grid-cols-2 gap-3 w-full">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="group p-4 rounded-2xl transition-all duration-300 hover:scale-[1.02]" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-9 h-9 rounded-xl bg-[#6dbf4a]/10 flex items-center justify-center mb-3 group-hover:bg-[#6dbf4a]/20 transition-colors">
                    <Icon className="w-4 h-4 text-[#6dbf4a]" />
                  </div>
                  <p className="text-sm font-semibold text-white">{f.label}</p>
                  <p className="text-xs text-white/30 mt-0.5">{f.desc}</p>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-white/15 mt-12 tracking-widest uppercase">
            Ventilation & VVS
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative overflow-hidden">
        {/* Subtle ambient */}
        <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-[100px] opacity-10" style={{ background: '#6dbf4a' }} />
        
        <div className="w-full max-w-sm relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-10">
            <img src={LOGO_DISPLAY} alt="Mälair" className="w-48 h-auto" style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }} />
          </div>

          <div className="space-y-8">
            <div className="space-y-3">
              <h1 className="text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Välkommen<span className="text-[#6dbf4a]">.</span>
              </h1>
              <p className="text-white/30 text-sm">Logga in för att fortsätta</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-white/40 text-xs font-medium">E-post</label>
                <Input
                  type="email"
                  placeholder="namn@malair.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#6dbf4a]/20 focus:border-[#6dbf4a]/40"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                  data-testid="login-email"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-white/40 text-xs font-medium">Lösenord</label>
                <Input
                  type="password"
                  placeholder="............"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#6dbf4a]/20 focus:border-[#6dbf4a]/40"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                  data-testid="login-password"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 gap-2 text-white font-bold text-sm rounded-xl transition-all duration-300"
                style={{ background: 'linear-gradient(135deg, #6dbf4a 0%, #4a9e2f 100%)', boxShadow: '0 4px 24px rgba(109,191,74,0.3), inset 0 1px 0 rgba(255,255,255,0.15)' }}
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Loggar in...</span>
                  </div>
                ) : (
                  <>Logga in <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
            </form>

            <p className="text-center text-xs text-white/15">
              Kontakta din admin om du behöver ett konto
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
